import React, { useEffect, useMemo, useReducer } from "react";
import toast from "react-hot-toast";
import {
  scanSharedContent,
  sharedContentIsEmpty,
  SHARED_SECTIONS,
} from "./sharedContent";

// D15 shared-content rail section (frames 5f desktop / 5l mobile). Renders the
// four groups — Media · Documents · Links · Business Cards — for the open
// thread, reverse-chronological, each entry with preview/icon, name, date,
// sender and a per-file download; clicking an entry jumps to its message.
//
// It scans the channel's currently-loaded messages (see sharedContent.js on the
// pagination stance) and re-derives on new messages. Reachable in read-only /
// archived threads (downloads stay; writing stays locked) — the section makes no
// write, so it is safe there (D15).
//
// jumpToMessage is owned by <Channel> (useChannelActionContext), which this rail
// sits OUTSIDE of; so an entry click dispatches a window event that a tiny
// listener inside <Channel> (CustomWindow) turns into the real jump — the same
// decoupling the participants sheet uses for its open trigger.

const LABEL_STYLE = {
  font: "600 9.5px ui-monospace,Menlo,monospace",
  color: "#98A2B3",
  letterSpacing: ".06em",
};

const DOC_CHIP_COLORS = {
  PDF: { bg: "#FDECEA", fg: "#C2362B" },
  XLS: { bg: "#EAF6EE", fg: "#17803D" },
  XLSX: { bg: "#EAF6EE", fg: "#17803D" },
  DOC: { bg: "#EAF2FF", fg: "#1466D8" },
  DOCX: { bg: "#EAF2FF", fg: "#1466D8" },
};

const DownloadGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 2v8m0 0L5 7m3 3l3-3" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 12.5h10" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const OpenGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6.5 3H3v10h10V9.5" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.5 3H13v3.5M13 3L7.5 8.5" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CopyGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="#98A2B3" strokeWidth="1.5" />
    <path d="M10.5 5.5V4a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const jumpTo = (messageId) => {
  if (!messageId) return;
  window.dispatchEvent(
    new CustomEvent("tp:threads:jump-to-message", { detail: { messageId } }),
  );
};

// Full-size preview — reuses CustomMessage's image-open pattern (a new tab via
// window.open, no new lightbox framework). Falls back to jumping to the message
// when the attachment has no direct URL to open.
const openPreview = (url, messageId) => {
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  jumpTo(messageId);
};

// Copy a shared link to the clipboard with a confirming toast — same helper
// shape used across the app (VerifyNowModal etc.).
const copyLink = async (url) => {
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied.");
  } catch (e) {
    toast.error("Couldn't copy the link.");
  }
};

const SharedContentSection = ({ channel, selfId, variant = "rail" }) => {
  const inSheet = variant === "sheet";
  // Re-scan when the loaded message set changes on the live channel.
  const [tick, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (!channel?.on) return undefined;
    const evts = ["message.new", "message.deleted", "message.updated"];
    const subs = evts.map((e) => channel.on(e, bump));
    return () => subs.forEach((s) => s?.unsubscribe && s.unsubscribe());
  }, [channel]);

  const buckets = useMemo(
    () => scanSharedContent(channel?.state?.messages || [], { selfId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channel, selfId, tick],
  );

  if (!channel) return null;
  const empty = sharedContentIsEmpty(buckets);

  const media = buckets[SHARED_SECTIONS.MEDIA];
  const documents = buckets[SHARED_SECTIONS.DOCUMENTS];
  const links = buckets[SHARED_SECTIONS.LINKS];
  const cards = buckets[SHARED_SECTIONS.BUSINESS_CARDS];

  const mediaTile = inSheet ? 72 : 58;

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {empty && (
        <>
          <span style={LABEL_STYLE}>SHARED</span>
          <p style={{ fontSize: 11.5, color: "#98A2B3", lineHeight: 1.5 }}>
            Files, links and cards shared in this chat show up here.
          </p>
        </>
      )}

      {/* MEDIA */}
      {media.length > 0 && (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span style={LABEL_STYLE}>SHARED — MEDIA · {media.length}</span>
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {media.slice(0, 3).map((m, i) => {
              const overflow = i === 2 && media.length > 3;
              return (
                <div
                  key={`${m.messageId}-${i}`}
                  className="group relative flex-shrink-0 overflow-hidden"
                  style={{ width: mediaTile, height: mediaTile, borderRadius: 9, background: "#E8EDF4" }}
                >
                  <button
                    type="button"
                    onClick={() => openPreview(m.url, m.messageId)}
                    title={m.name}
                    aria-label={`Open ${m.name}`}
                    className="block h-full w-full"
                  >
                    {m.thumb ? (
                      <img src={m.thumb} alt={m.name} className="h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-[#98A2B3]">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="3" y="4" width="18" height="16" rx="2" strokeWidth="1.6" />
                          <circle cx="8.5" cy="9.5" r="1.5" strokeWidth="1.6" />
                          <path d="M4 17l5-5 4 4 3-3 4 4" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    )}
                    {overflow && (
                      <span
                        className="absolute inset-0 flex items-center justify-center text-white"
                        style={{ background: "rgba(16,24,40,.55)", fontSize: 15, fontWeight: 700 }}
                      >
                        +{media.length - 2}
                      </span>
                    )}
                  </button>
                  {m.url && !overflow && (
                    <a
                      href={m.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Download ${m.name}`}
                      title="Download"
                      className="absolute right-1 top-1 hidden items-center justify-center rounded-md p-1 group-hover:flex"
                      style={{ background: "rgba(16,24,40,.62)" }}
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M8 2v8m0 0L5 7m3 3l3-3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M3 12.5h10" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DOCUMENTS */}
      {documents.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL_STYLE}>DOCUMENTS · {documents.length}</span>
          {documents.map((d, i) => {
            const chip = DOC_CHIP_COLORS[d.kindLabel] || { bg: "#F2F4F7", fg: "#475467" };
            return (
              <div key={`${d.messageId}-${i}`} className="flex items-center" style={{ gap: 9 }}>
                <button
                  type="button"
                  onClick={() => jumpTo(d.messageId)}
                  className="flex min-w-0 flex-1 items-center text-left"
                  style={{ gap: 9 }}
                >
                  <span
                    className="flex flex-shrink-0 items-center justify-center"
                    style={{ width: 30, height: 30, borderRadius: 8, background: chip.bg, color: chip.fg, fontSize: 8.5, fontWeight: 700 }}
                  >
                    {d.kindLabel.slice(0, 4)}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: "#101828" }}>
                      {d.name}
                    </span>
                    <span className="truncate" style={{ fontSize: 10.5, color: "#98A2B3" }}>
                      {[d.date, d.sender, d.size].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </button>
                {d.url && (
                  <a
                    href={d.url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 rounded-md p-1 hover:bg-[#F0F2F6]"
                    aria-label={`Download ${d.name}`}
                  >
                    <DownloadGlyph />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* LINKS */}
      {links.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL_STYLE}>LINKS · {links.length}</span>
          {links.map((l, i) => (
            <div key={`${l.messageId}-${i}`} className="group flex items-center" style={{ gap: 9 }}>
              {/* Text click still jumps to the source message. */}
              <button
                type="button"
                onClick={() => jumpTo(l.messageId)}
                className="flex min-w-0 flex-1 items-center text-left"
                style={{ gap: 9 }}
              >
                <span
                  className="flex flex-shrink-0 items-center justify-center"
                  style={{ width: 30, height: 30, borderRadius: 8, background: "#F0F6FF", color: "#1466D8" }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: "#101828" }}>
                    {l.name}
                  </span>
                  <span className="truncate" style={{ fontSize: 10.5, color: "#98A2B3" }}>
                    {[l.date, l.sender, l.host].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </button>
              {/* Open / Copy only when the entry carries a real URL (pure
                  in-app cards have none — those stay jump-only). */}
              {l.url && (
                <div className="flex flex-shrink-0 items-center" style={{ gap: 2 }}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="rounded-md p-1 hover:bg-[#F0F2F6]"
                    aria-label={`Open ${l.name}`}
                    title="Open in new tab"
                  >
                    <OpenGlyph />
                  </a>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyLink(l.url);
                    }}
                    className="rounded-md p-1 hover:bg-[#F0F2F6]"
                    aria-label={`Copy ${l.name}`}
                    title="Copy link"
                  >
                    <CopyGlyph />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* BUSINESS CARDS */}
      {cards.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL_STYLE}>BUSINESS CARDS · {cards.length}</span>
          {cards.map((c, i) => (
            <button
              key={`${c.messageId}-${i}`}
              type="button"
              onClick={() => jumpTo(c.messageId)}
              className="flex items-center text-left"
              style={{ gap: 9 }}
            >
              <span
                className="flex flex-shrink-0 items-center justify-center"
                style={{ width: 30, height: 30, borderRadius: 8, background: "#F4F0FF", color: "#6941C6" }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth="1.6" />
                  <circle cx="8" cy="11" r="2" strokeWidth="1.6" />
                  <path d="M13 10h5M13 13h5M5 15.5c.6-1.3 1.7-2 3-2s2.4.7 3 2" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: "#101828" }}>
                  {c.name}
                </span>
                <span className="truncate" style={{ fontSize: 10.5, color: "#98A2B3" }}>
                  {[c.date, c.sender].filter(Boolean).join(" · ")}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SharedContentSection;
